import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { Iconify, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
import useAuth from '@/hooks/useAuth';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useToggle from '@/hooks/useToggle';
import {
  deleteAuthSession,
  getAuthSessions,
  revokeAllAuthSessions,
} from '@/features/users/shared/api';
import type { AuthSession } from '@/features/users/shared/types';
import UserSessionsTableRow from './UserSessionsTableRow';
import {
  USER_SESSIONS_TABLE_HEAD,
  applySortFilterUserSessions,
} from '../utils/user-sessions';

type UserSessionsProps = {
  capabilityAwareActions?: boolean;
};

export default function UserSessions({
  capabilityAwareActions = false,
}: UserSessionsProps) {
  const { userId } = useParams();
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const canRevokeAllSessions = capabilityAwareActions && isSuperAdmin;
  const {
    toggle: isRevokeAllDialogOpen,
    onOpen: openRevokeAllDialog,
    onClose: closeRevokeAllDialog,
  } = useToggle();

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrder: 'desc',
    defaultOrderBy: 'lastSeenAt',
  });

  const { data: sessions = [], isLoading: isSessionsLoading } = useQuery({
    queryKey: ['userSessions', userId],
    queryFn: () => getAuthSessions(String(userId)),
    enabled: !!userId,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching active sessions');
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) =>
      deleteAuthSession(sessionId, String(userId)),
    onSuccess: () => {
      queryClient.invalidateQueries(['userSessions', userId]);
      successMsg('Session removed successfully');
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while removing session');
    },
  });

  const revokeAllSessionsMutation = useMutation({
    mutationFn: () => revokeAllAuthSessions(String(userId)),
    onSuccess: () => {
      closeRevokeAllDialog();
      queryClient.invalidateQueries(['userSessions', userId]);
      successMsg('All sessions revoked successfully');
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while revoking all sessions');
    },
  });

  const handleRevokeAllSessions = () => {
    if (!userId || !canRevokeAllSessions) return;
    revokeAllSessionsMutation.mutate();
  };

  const handleFilterSearchValue = (filterName: string) => {
    setSearchFieldValue(filterName);
    setPage(0);
  };

  const dataFiltered = applySortFilterUserSessions({
    tableData: sessions,
    comparator: getComparator<AuthSession>(order, orderBy as keyof AuthSession),
    searchFieldValue,
  });

  const denseHeight = dense ? 52 : 72;
  const isNotFound =
    (!dataFiltered.length && !!searchFieldValue) || !dataFiltered.length;

  return (
    <>
      {canRevokeAllSessions ? (
        <DialogSimple
          dialogTitle="Revoke all sessions"
          open={isRevokeAllDialogOpen}
          onClickButtonClose={closeRevokeAllDialog}
          onClickButtonLoading={handleRevokeAllSessions}
          loadingButton
          loadingButtonText="Revoke all sessions"
          loadingButtonColor="error"
          disagreeButton
          disagreeButtonText="Cancel"
          disagreeButtonColor="inherit"
          isSubmitting={revokeAllSessionsMutation.isLoading}
        >
          <Box sx={{ px: 3 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This will delete Redis sessions and revoke Firebase refresh
              tokens, forcing the provider to log in again on all devices.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use this for guaranteed logout across all active sessions.
            </Typography>
          </Box>
        </DialogSimple>
      ) : null}

      <Card>
        {isSessionsLoading ? (
          <SkeletonLoadingTable />
        ) : (
          <>
            <ToolbarWithSearchAndStatus
              filterText={searchFieldValue}
              onFilterText={handleFilterSearchValue}
              labels={{ search: 'Search active sessions...' }}
              action={
                canRevokeAllSessions ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Iconify icon="eva:log-out-outline" />}
                    onClick={openRevokeAllDialog}
                    disabled={!userId || revokeAllSessionsMutation.isLoading}
                  >
                    Revoke all sessions
                  </Button>
                ) : undefined
              }
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 980, position: 'relative' }}>
                <Table size={dense ? 'small' : 'medium'}>
                  <TableHeadCustom
                    order={order}
                    orderBy={orderBy}
                    headLabel={USER_SESSIONS_TABLE_HEAD}
                    rowCount={dataFiltered.length}
                    numSelected={selected.length}
                    onSort={onSort}
                  />
                  <TableBody>
                    {dataFiltered
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((session) => (
                        <UserSessionsTableRow
                          key={session.sessionId}
                          row={session}
                          isDeleting={
                            deleteSessionMutation.isLoading &&
                            deleteSessionMutation.variables ===
                              session.sessionId
                          }
                          capabilityAwareActions={capabilityAwareActions}
                          canRevokeAllSessions={canRevokeAllSessions}
                          onDeleteRow={deleteSessionMutation.mutate}
                        />
                      ))}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(
                        page,
                        rowsPerPage,
                        dataFiltered.length
                      )}
                    />

                    <TableNoData
                      isNotFound={isNotFound}
                      title="No active sessions found"
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={dataFiltered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
              />

              <FormControlLabel
                control={<Switch checked={dense} onChange={onChangeDense} />}
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
              />
            </Box>
          </>
        )}
      </Card>
    </>
  );
}
